import { v4 as uuidv4 } from 'uuid';
import { isNumeric } from 'mathjs';
import TextUtils from "./TextUtils";
import { OperatingSystems } from '../../globals';

// thanks to https://stackoverflow.com/a/38340730/618726 and chatGPT
export function removeUndefinedEntriesFromObjectRecursive(obj: any): any
{
	if (obj === null || obj === undefined) return {};

	if (Array.isArray(obj)) 
	{
		return obj
			.filter(item => item !== undefined && item !== null)
			.map(item => 
				item === Object(item) ? removeUndefinedEntriesFromObjectRecursive(item) : item
			);
	}

	if (typeof obj === 'object') 
	{
		return Object.fromEntries(
			Object.entries(obj)
				.filter(([_, v]) => v !== undefined && v !== null)
				.map(([k, v]) => [k, v === Object(v) ? removeUndefinedEntriesFromObjectRecursive(v) : v])
		);
	}

	return obj;
}

/**
 * use to get an array of values that are shared between all the input arrays
 * for example:
 * intersectArrays([1, 2, 3], [2, 3, 4], [3, 4, 5]) => [3]
 * intersectArrays(...[ ["a", "b"], ["b", "c"], ["c", "d"] ]) => ["b", "c"] << note that when passing an array of arrays, you must use the spread operator
 *
 * @export
 * @template T
 * @param {...T[][]} arrays
 * @returns {T[]}
 */
export function intersectArrays<T>(...arrays: T[][]): T[]
{
	if(arrays.length === 0) return [];
	if(arrays.length === 1) return arrays[0];

	return arrays.reduce((a, b) => 
	{
		const set = new Set(b);
		return a.filter(x => set.has(x));
	});
}

export function JsonCopy<T = any>(obj: T): T
{
	if(obj === undefined) return undefined as any;
	return JSON.parse(JSON.stringify(obj)) as T;
}

export function enumFromString<T extends Record<string, any>>(enumType: T, value: string): T[keyof T] | undefined
{
	return Object.values(enumType).find((v) => v === value);
}

export function enumToDropdownOptions<T extends object>(enumObj: T): { display: string; value: T[keyof T] }[] 
{
	return (Object.keys(enumObj) as Array<keyof T>)
		.filter((key): key is keyof T => isNaN(Number(key)))
		.map(key => ({
			display: TextUtils.prettifyText(String(key)),
			value: enumObj[key]
		}));
}

export function isRBOP(files: string[]) 
{
	return files.find((file) => file === 'RealityBOP.json') !== undefined;
}

export function removeURLParam(key: string)
{
	const sourceURL = window.location.href;

	let url = sourceURL.split("?")[0],
		param,
		params_arr:string[] = []
	const queryString = (sourceURL.indexOf("?") !== -1) ? sourceURL.split("?")[1] : "";
	if (queryString !== "")
	{
		params_arr = queryString.split("&");
		for (let i = params_arr.length - 1; i >= 0; i -= 1)
		{
			param = params_arr[i].split("=")[0];
			if (param === key)
			{
				params_arr.splice(i, 1);
			}
		}
		if (params_arr.length) url = url + "?" + params_arr.join("&");
	}

	window.history.pushState('', document.title, url);
}

export function addURLParam(key: string, value: string)
{
	window.history.pushState({}, "", `/?${key}=${value}`);
}

export function replaceURLParam(key: string, value: string)
{
	removeURLParam(key);
	addURLParam(key, value);
}

/**
 * uses 'uuid' package to generate a unique ID
 * can generate in place instead of importing this using:
 * import { v4 as uuidv4 } from 'uuid';
 * uuidv4();
 * @static
 * @returns
 * @memberof Utils
 */
export function getUuidv4(): string
{
	return uuidv4();
}

export function traverseJSObject<T extends object>(obj: T, fn: (obj: T, key: string, value: any) => void): void
{
	if(!obj || typeof(obj) !== "object" || Array.isArray(obj)) return;

	for(const [key, value] of Object.entries(obj))
	{
		fn?.(obj, key, value);
		traverseJSObject(value, fn);
	}
}

/**
 * a shorthand method for checking that a given object exists, is not empty, and is not a function
 * - with help from SO: https://stackoverflow.com/a/32108184/618726
 * @static
 * @param {*} obj the thing to check; it could be anything really
 * @returns true if obj is a defined, existing object with any members
 * @memberof Utils
 */
export function isValidJSObject(obj: any)
{
	return obj !== null &&
		obj !== undefined &&
		Object.keys(obj).length > 0 &&
		obj.constructor === Object;
}

/**
 * A shorthand method for checking whether a given object is iterable
 * - taken from SO: https://stackoverflow.com/questions/18884249/checking-whether-something-is-iterable/53106917
 * @static
 * @param {*} obj
 * @returns true if the given parameter is iterable, false otherwise
 * @memberof Utils
 */
export function isIterable(obj: any)
{
	// checks for null and undefined
	if (obj === null || obj === undefined)
	{
		return false;
	}
	return typeof obj[Symbol.iterator] === 'function';
}

/**
 * A shorthand method for checking whether a given object is a number
 *
 * @export
 * @param {*} value
 * @return {*}  {boolean}
 */
export function isNumber(value: any): boolean
{
	return typeof value !== 'boolean' && !Array.isArray(value) && isNumeric(value);
}

/**
 * anything that is not null or undefined
 *
 * @static
 * @param {*} obj
 * @returns {boolean}
 * @memberof Utils
 */
export function isTangible(obj: any): boolean
{
	return obj !== null && obj !== undefined;
}

/**
 * Returns an enum indicating which operating system the user is on
 *
 * @export
 * @return {*}  {OperatingSystems}
 */
export function getOperationSystem(): OperatingSystems
{
	//https://stackoverflow.com/questions/9514179/how-to-find-the-operating-system-version-using-javascript/9514476#9514476
	const userAgent = window.navigator.userAgent;
	const platform = window.navigator.platform;
	const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
	const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
	const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

	if (windowsPlatforms.indexOf(platform) !== -1) return OperatingSystems.Windows;
	if (macosPlatforms.indexOf(platform) !== -1) return OperatingSystems.MacOS;
	if (iosPlatforms.indexOf(platform) !== -1) return OperatingSystems.IOS;
	if (/Android/.test(userAgent)) return OperatingSystems.ANDROID;
	if (/Linux/.test(platform)) return OperatingSystems.LINUX;

	return OperatingSystems.UNKNOWN;
}

/**
 * check if given array a is practically identical to given array b by iterating them both and comparing the values at each index
 * @static
 * @template T
 * @param {T[]} a
 * @param {T[]} b
 * @returns true if both arrays are of equal size *and* each of the elements in a is equal to the corresponding element in b
 * @memberof Utils
 */
export function areArraysEqual<T>(a: T[], b: T[]): boolean
{
	if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
	else
	{
		// comapring each element of array
		for (let i = 0; i < a.length; i++)
		{ if (a[i] !== b[i]) return false; }
		return true;
	}
}

/**
 * checks that the values in array a and b are the same, regardless of order  
 * uses sets internally for this check, so the arrays must be of the same size after being converted to sets
 *
 * @export
 * @template T
 * @param {T[]} a
 * @param {T[]} b
 * @return {*}  {boolean} false if the arrays are of different sizes or contain different values, true otherwise
 */
export function areArrayEqualUnordered<T>(a: T[], b: T[]): boolean
{
	if(a.length !== b.length) return false;

	const setA = new Set(a);
	const setB = new Set(b);

	if(setA.size !== setB.size) return false;

	for(const value of setA)
	{
		if(!setB.has(value)) return false;
	}

	return true;
}

/**
 * Check if an object implements the given interface
 * @static
 * @param {Object} object the object to check
 * @param {Array<keyof>} interfaceKeys keys of the interface to check (note: typing can't be checked)
 * @memberof Utils
 */
export function isInterface(object: any, interfaceKeys: string[])
{
	if(!object || typeof object !== "object") return false;
	const objectKeys = Object.keys(object);
	if(objectKeys.length < interfaceKeys.length) return false; // extra keys in object are allowed
	return interfaceKeys.every(key => objectKeys.includes(key));
}

/**
 * check the euqlity of a and b to an arbitrary depth and nesting levels
 *
 * @static
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 * @memberof Utils
 */
export function deepEqual(a: any, b: any): boolean
{
	if(a === b)
	{
		return true;
	}

	// null !== undefined :D
	if(a === null || b === null || typeof a !== "object" || typeof b !== "object")
	{
		return false;
	}

	const keysA = Object.keys(a);
	const keysB = Object.keys(b);

	if(keysA.length !== keysB.length)
	{
		return false;
	}

	for(const key of keysA)
	{
		if(!keysB.includes(key) || !deepEqual(a[key], b[key]))
		{
			return false;
		}
	}

	return true;
}

/**
 * if `toCopy` is an object, return a deep copy of it using JsonCopy  
 * else, it shouldn't be possible to copy it so we return as-is
 *
 * @export
 * @param {*} toCopy
 * @return {*} 
 */
export function copyValueOrObject(toCopy: any)
{
	return typeof toCopy === 'object' && toCopy !== null ? JsonCopy(toCopy) : toCopy;
}